import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FlaskConical,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  FileJson,
  FileText,
  FileSpreadsheet,
  RefreshCw,
  MessageSquare,
  Boxes,
  Gauge,
  ChevronDown,
  KeyRound,
  Search,
  Copy,
  Check,
} from 'lucide-react';
import api from '../api/client';
import Breadcrumbs from '../components/Breadcrumbs';
import ErrorBoundary from '../components/ErrorBoundary';

const CATEGORY_LABELS = {
  chat: 'Chat',
  embedding: 'Embedding',
  rateLimited: 'Rate-Limited',
  mcp: 'MCP Server',
  agent: 'Agent',
  other: 'Other',
};

// Per-category visual accents (icon + badge colors) for the discovery cards.
const CATEGORY_STYLES = {
  chat: { icon: MessageSquare, badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  embedding: { icon: Boxes, badge: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  rateLimited: { icon: Gauge, badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  mcp: { icon: FlaskConical, badge: 'bg-teal-50 text-teal-700 border-teal-200', dot: 'bg-teal-500' },
  agent: { icon: MessageSquare, badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  other: { icon: FlaskConical, badge: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
};

// APIM-native kinds — mirror the portal's three buckets. Order is fixed so the
// page always reads API → MCP → Agent.
const KIND_ORDER = ['api', 'mcp', 'agent'];
const KIND_META = {
  api: { label: 'APIs', icon: Boxes, desc: 'Model & service APIs (OpenAI-style chat / embeddings).' },
  mcp: { label: 'MCP Servers', icon: FlaskConical, desc: 'Model Context Protocol servers (JSON-RPC tools).' },
  agent: { label: 'Agents', icon: MessageSquare, desc: 'A2A agents.' },
};

// Normalize token usage across endpoint shapes. /chat/completions uses
// prompt_tokens/completion_tokens; /responses uses input_tokens/output_tokens.
// Returns a friendly object the UI can render + a plain-English tooltip.
function tokenInfo(usage) {
  if (!usage) return { label: '—', title: 'No token usage reported' };
  const prompt = usage.prompt_tokens ?? usage.input_tokens;
  const completion = usage.completion_tokens ?? usage.output_tokens;
  const total = usage.total_tokens ?? (((prompt ?? 0) + (completion ?? 0)) || undefined);
  const parts = [];
  if (prompt != null) parts.push(`${prompt} in`);
  if (completion != null) parts.push(`${completion} out`);
  const label = total != null ? String(total) : (parts.length ? '—' : '—');
  const detail = parts.length ? ` (${parts.join(' · ')})` : '';
  return {
    label: `${label}${detail}`,
    title: `Total ${total ?? '?'} tokens — ${prompt ?? '?'} input (your prompt), ${completion ?? '?'} output (model reply)`,
  };
}

// Human-friendly latency: shows ms under 1s, else seconds with one decimal.
function formatLatency(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function StatusPill({ status }) {
  const ok = status === 'success';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${
        ok
          ? 'bg-green-50 text-green-700 ring-green-200'
          : 'bg-red-50 text-red-700 ring-red-200'
      }`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {ok ? 'Passed' : 'Failed'}
    </span>
  );
}

// Format an ISO timestamp into a readable local date/time.
function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Small button that copies text to the clipboard and briefly shows a check.
function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  }
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-sce-green transition"
      title={`${label} to clipboard`}
    >
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

export default function ModelTesting() {
  const [subscriptionKey, setSubscriptionKey] = useState('');
  const [prompt, setPrompt] = useState('');
  const [suite, setSuite] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  // Per-model individual test state.
  const [testingId, setTestingId] = useState(null);
  const [singleResults, setSingleResults] = useState({}); // { [apiId]: result }
  const [expanded, setExpanded] = useState({}); // { [apiId]: bool } — inline detail toggle
  // Suite results view state.
  const [rowExpanded, setRowExpanded] = useState({}); // { [rowKey]: bool } — desktop detail row
  const [resultFilter, setResultFilter] = useState('all'); // 'all' | 'passed' | 'failed'
  const [search, setSearch] = useState('');

  // Discover + classify APIs (read-only, safe to auto-load).
  const {
    data: apisResp,
    isLoading: apisLoading,
    error: apisError,
    refetch,
  } = useQuery({
    queryKey: ['model-test-apis'],
    queryFn: () => api.getModelTestApis(),
  });

  const apis = apisResp?.data || [];
  // Group by APIM-native KIND first (API / MCP / Agent) so the portal's three
  // buckets are mirrored, then by test category within each kind.
  const byKind = apis.reduce(
    (acc, a) => {
      const k = a.kind || 'api';
      (acc[k] ??= []).push(a);
      return acc;
    },
    { api: [], mcp: [], agent: [] }
  );

  const canRun = !running && !testingId;

  async function runSuite() {
    setError(null);
    setRunning(true);
    try {
      const report = await api.runModelTestSuite({
        subscriptionKey: subscriptionKey.trim() || undefined,
        prompt: prompt.trim() || undefined,
      });
      report._runAt = new Date().toISOString();
      report._promptUsed = prompt.trim() || '(built-in sample prompt)';
      setSuite(report);
      setResultFilter('all');
      setSearch('');
      setRowExpanded({});
    } catch (e) {
      setError(e.message || 'Suite failed');
    } finally {
      setRunning(false);
    }
  }

  async function testModel(apiId) {
    setError(null);
    setTestingId(apiId);
    try {
      const result = await api.runModelTest({
        apiId,
        subscriptionKey: subscriptionKey.trim() || undefined,
        prompt: prompt.trim() || undefined,
      });
      setSingleResults((prev) => ({ ...prev, [apiId]: result }));
      setExpanded((prev) => ({ ...prev, [apiId]: true }));
    } catch (e) {
      setSingleResults((prev) => ({
        ...prev,
        [apiId]: { apiId, status: 'error', error: e.message || 'Test failed' },
      }));
    } finally {
      setTestingId(null);
    }
  }

  // Derived: results filtered by pass/fail toggle + free-text search.
  const filteredResults = useMemo(() => {
    const list = suite?.results || [];
    const q = search.trim().toLowerCase();
    return list.filter((r) => {
      if (resultFilter === 'passed' && r.status !== 'success') return false;
      if (resultFilter === 'failed' && r.status === 'success') return false;
      if (!q) return true;
      const hay = [
        r.displayName,
        r.apiId,
        CATEGORY_LABELS[r.category] || r.category,
        r.error,
        r.content || r.responseBody,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [suite, resultFilter, search]);

  function triggerDownload(filename, content, mime) {    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function downloadJson() {
    if (!suite) return;
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    triggerDownload(
      `model-test-report-${ts}.json`,
      JSON.stringify(suite, null, 2),
      'application/json'
    );
  }

  function downloadCsv() {
    if (!suite) return;
    const fileTs = new Date().toISOString().replace(/[:.]/g, '-');
    const cell = (v) => {
      const s = String(v ?? '').replace(/"/g, '""');
      return `"${s}"`;
    };
    const headers = [
      'Model',
      'Type',
      'Result',
      'HTTP',
      'Latency (ms)',
      'Tokens (total)',
      'Prompt Sent',
      'Response',
      'Error',
      'Endpoint',
    ];
    const lines = [headers.join(',')];
    for (const r of suite.results || []) {
      lines.push(
        [
          cell(r.displayName || r.apiId),
          cell(CATEGORY_LABELS[r.category] || r.category),
          cell(r.status === 'success' ? 'Passed' : 'Failed'),
          cell(r.httpStatus || ''),
          cell(r.latencyMs ?? ''),
          cell(tokenInfo(r.tokenUsage).label),
          cell(r.promptSent || ''),
          cell(r.content || r.responseBody || ''),
          cell(r.error || ''),
          cell(r.url || ''),
        ].join(',')
      );
    }
    triggerDownload(`model-test-report-${fileTs}.csv`, lines.join('\n'), 'text/csv');
  }

  function downloadHtml() {
    if (!suite) return;
    const ts = suite._runAt || new Date().toISOString();
    const fileTs = ts.replace(/[:.]/g, '-');
    const esc = (v) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const rows = (suite.results || [])
      .map(
        (r) => `      <tr class="${r.status === 'success' ? 'ok' : 'fail'}">
        <td>${esc(r.displayName || r.apiId)}</td>
        <td>${esc(CATEGORY_LABELS[r.category] || r.category)}</td>
        <td>${esc(r.status === 'success' ? 'Passed' : 'Failed')}</td>
        <td>${esc(formatLatency(r.latencyMs))}</td>
        <td><pre>${esc(r.promptSent || '')}</pre></td>
        <td><pre>${esc(r.content || r.responseBody || '')}</pre></td>
        <td>${esc(tokenInfo(r.tokenUsage).label)}</td>
        <td>${esc(r.error || '')}</td>
        <td>${esc(r.url || '')}</td>
      </tr>`
      )
      .join('\n');
    const byCat = (suite.results || []).reduce((acc, r) => {
      const key = CATEGORY_LABELS[r.category] || r.category;
      acc[key] ??= { total: 0, passed: 0 };
      acc[key].total += 1;
      if (r.status === 'success') acc[key].passed += 1;
      return acc;
    }, {});
    const rollup = Object.entries(byCat)
      .map(
        ([cat, v]) =>
          `<span class="chip">${esc(cat)}: ${v.passed}/${v.total} passed</span>`
      )
      .join(' ');
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>AI Model Test Report — ${esc(formatDateTime(ts))}</title>
<style>
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; margin: 2rem; color: #1f2937; }
  h1 { font-size: 1.25rem; }
  .meta { color: #6b7280; font-size: 0.85rem; margin: 0.25rem 0 1rem; }
  .prompt { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0.5rem 0.75rem; font-size: 0.85rem; margin: 0.5rem 0 1rem; white-space: pre-wrap; word-break: break-word; }
  .summary { display: flex; gap: 1rem; margin: 1rem 0; flex-wrap: wrap; }
  .card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0.5rem 1rem; font-size: 0.9rem; }
  .chips { margin: 0.5rem 0 1rem; }
  .chip { display: inline-block; background: #eef2ff; color: #3730a3; border: 1px solid #c7d2fe; border-radius: 999px; padding: 2px 10px; font-size: 0.75rem; margin: 0 4px 4px 0; }
  table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 10px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.05em; }
  td pre { margin: 0; white-space: pre-wrap; word-break: break-word; max-width: 320px; font-family: ui-monospace, monospace; font-size: 0.75rem; }
  tr.ok td:nth-child(3) { color: #047857; font-weight: 600; }
  tr.fail td:nth-child(3) { color: #b91c1c; font-weight: 600; }
</style>
</head>
<body>
  <h1>AI Model Test Report</h1>
  <div class="meta">Generated: ${esc(formatDateTime(ts))}</div>
  <div class="prompt"><strong>Prompt used:</strong> ${esc(suite._promptUsed || '(built-in sample prompt)')}</div>
  <div class="summary">
    <div class="card"><strong>${esc(suite.summary.passRate)}%</strong> passed</div>
    <div class="card">${esc(suite.summary.passed)} of ${esc(suite.summary.total)} models working</div>
    <div class="card">Avg response time: ${esc(formatLatency(suite.summary.avgLatencyMs))}</div>
  </div>
  <div class="chips">${rollup}</div>
  <table>
    <thead>
      <tr>
        <th>Model</th><th>Type</th><th>Result</th>
        <th>Speed</th><th>Prompt Sent</th><th>Response</th><th>Tokens (total, in/out)</th><th>Error</th><th>Endpoint</th>
      </tr>
    </thead>
    <tbody>
${rows}
    </tbody>
  </table>
</body>
</html>`;
    triggerDownload(`model-test-report-${fileTs}.html`, html, 'text/html');
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6 sm:space-y-8">
        <Breadcrumbs />

        {/* Page header */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl bg-sce-green/10 text-sce-green">
            <FlaskConical className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Model Testing</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Discover AI APIs published through APIM and run live smoke tests through the gateway.
            </p>
          </div>
        </div>

        {/* Controls */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6 space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            {/* Subscription key */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <KeyRound className="w-4 h-4 text-gray-400" />
                Gateway subscription key <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                type="password"
                value={subscriptionKey}
                onChange={(e) => setSubscriptionKey(e.target.value)}
                placeholder="Only if the API requires an API key"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sce-green focus:ring-2 focus:ring-sce-green/20 outline-none transition"
              />
              <p className="text-xs text-gray-400">
                Your signed-in user token is sent as the Bearer credential automatically.
                Add a subscription key only for APIs that require one. Never stored on the server.
              </p>
            </div>

            {/* Prompt */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <MessageSquare className="w-4 h-4 text-gray-400" />
                Test prompt <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={2}
                placeholder="e.g. Summarize the benefits of retrieval-augmented generation in one sentence."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sce-green focus:ring-2 focus:ring-sce-green/20 outline-none transition resize-none"
              />
              <p className="text-xs text-gray-400">
                Custom input sent to each model. Leave blank to use a built-in sample prompt.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center border-t border-gray-100 pt-4">
            <button
              onClick={runSuite}
              disabled={!canRun}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-sce-green text-white rounded-lg text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {running ? 'Running…' : 'Test All Models'}
            </button>
            <button
              onClick={() => refetch()}
              disabled={running || !!testingId}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh model list
            </button>
            {error && (
              <div className="inline-flex items-center gap-1.5 text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
                <XCircle className="w-4 h-4" />
                {error}
              </div>
            )}
          </div>
        </section>

        {/* Discovered APIs */}
        <section>
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            Available Models
            {apis.length > 0 && (
              <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                {apis.length}
              </span>
            )}
          </h2>
          {apisLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Discovering…
            </div>
          )}
          {apisError && (
            <div className="text-sm text-red-600">{apisError.message}</div>
          )}
          {!apisLoading && !apisError && apis.length === 0 && (
            <div className="text-sm text-gray-500">No APIs found.</div>
          )}
          {KIND_ORDER.map((kind) => {
            const kindList = byKind[kind] || [];
            if (kindList.length === 0) return null;
            const meta = KIND_META[kind];
            const KindIcon = meta.icon;
            // Within a kind, group by test category for the accent cards.
            const byCategory = kindList.reduce((acc, a) => {
              (acc[a.category || 'other'] ??= []).push(a);
              return acc;
            }, {});
            return (
              <div key={kind} className="mb-8 last:mb-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sce-green/10 text-sce-green">
                    <KindIcon className="w-4 h-4" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {meta.label}
                      <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                        {kindList.length}
                      </span>
                    </h3>
                    <p className="text-xs text-gray-400">{meta.desc}</p>
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(byCategory).map(([cat, list]) => {
                    const style = CATEGORY_STYLES[cat] || CATEGORY_STYLES.other;
                    const CatIcon = style.icon;
                    return (
                <div
                  key={cat}
                  className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg border ${style.badge}`}>
                      <CatIcon className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-semibold text-gray-800">
                      {CATEGORY_LABELS[cat] || cat}
                    </span>
                    <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                      {list.length}
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {list.map((a) => {
                      const res = singleResults[a.id];
                      const isTesting = testingId === a.id;
                      return (
                        <li key={a.id} className="px-4 py-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className="text-sm font-medium text-gray-800 truncate"
                              title={a.displayName || a.id}
                            >
                              {a.displayName || a.id}
                            </span>
                            <button
                              onClick={() => testModel(a.id)}
                              disabled={isTesting || running}
                              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-sce-green disabled:opacity-50 transition"
                            >
                              {isTesting ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Play className="w-3 h-3" />
                              )}
                              Test
                            </button>
                          </div>
                          {res && (
                            <div className="text-xs text-gray-500 space-y-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <StatusPill status={res.status} />
                                {res.httpStatus && (
                                  <span className="text-gray-400">HTTP {res.httpStatus}</span>
                                )}
                                {res.latencyMs != null && (
                                  <span className="text-gray-400">{formatLatency(res.latencyMs)}</span>
                                )}
                                <button
                                  onClick={() =>
                                    setExpanded((prev) => ({ ...prev, [a.id]: !prev[a.id] }))
                                  }
                                  className="ml-auto inline-flex items-center gap-0.5 text-sce-green font-medium hover:underline"
                                >
                                  {expanded[a.id] ? 'Hide' : 'Details'}
                                  <ChevronDown
                                    className={`w-3 h-3 transition-transform ${
                                      expanded[a.id] ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>
                              </div>
                              {res.error && (
                                <div
                                  className="text-red-600 break-words bg-red-50 rounded-md px-2 py-1 flex items-start justify-between gap-2"
                                  title={res.wwwAuthenticate || res.error}
                                >
                                  <span>{res.error}</span>
                                  <CopyButton text={res.wwwAuthenticate || res.error} label="Copy" />
                                </div>
                              )}
                              {expanded[a.id] && (
                                <div className="space-y-2 rounded-lg bg-gray-50 border border-gray-100 p-2.5">
                                  {res.promptSent && (
                                    <div>
                                      <div className="flex items-center justify-between mb-0.5">
                                        <span className="font-semibold text-gray-600">Prompt sent</span>
                                        <CopyButton text={res.promptSent} />
                                      </div>
                                      <pre className="whitespace-pre-wrap break-words text-gray-700 font-mono">
                                        {res.promptSent}
                                      </pre>
                                    </div>
                                  )}
                                  {(res.content || res.responseBody) && (
                                    <div>
                                      <div className="flex items-center justify-between mb-0.5">
                                        <span className="font-semibold text-gray-600">Response</span>
                                        <CopyButton text={res.content || res.responseBody} />
                                      </div>
                                      <pre className="whitespace-pre-wrap break-words text-gray-700 font-mono max-h-48 overflow-auto">
                                        {res.content || res.responseBody}
                                      </pre>
                                    </div>
                                  )}
                                  {res.tokenUsage && (
                                    <div className="text-gray-500" title={tokenInfo(res.tokenUsage).title}>
                                      <span className="font-semibold text-gray-600">Tokens: </span>
                                      {tokenInfo(res.tokenUsage).label}
                                    </div>
                                  )}
                                  {res.url && (
                                    <div className="text-gray-400 break-all font-mono" title={res.url}>
                                      {res.url}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
                  })}
                </div>
              </div>
            );
          })}
        </section>

        {/* Suite results */}
        {suite && (
          <section>
            <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Test Results</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ran {formatDateTime(suite._runAt)} · “Passed” means the model replied
                  successfully. “Tokens” shows how much text was processed (input + output).
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  <span className="font-medium text-gray-600">Prompt used:</span>{' '}
                  <span className="italic">{suite._promptUsed}</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  CSV
                </button>
                <button
                  onClick={downloadJson}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  <FileJson className="w-3.5 h-3.5" />
                  JSON
                </button>
                <button
                  onClick={downloadHtml}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  <FileText className="w-3.5 h-3.5" />
                  HTML
                </button>
              </div>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-5">
              <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3">
                <div className="text-2xl font-bold text-green-700">{suite.summary.passRate}%</div>
                <div className="text-xs font-medium text-green-600/80">Success rate</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="text-2xl font-bold text-gray-900">
                  {suite.summary.passed}<span className="text-gray-400 text-lg">/{suite.summary.total}</span>
                </div>
                <div className="text-xs font-medium text-gray-500">Models working</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className={`text-2xl font-bold ${suite.summary.failed ? 'text-red-600' : 'text-gray-900'}`}>
                  {suite.summary.failed}
                </div>
                <div className="text-xs font-medium text-gray-500">Not working</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="text-2xl font-bold text-gray-900">
                  {formatLatency(suite.summary.avgLatencyMs)}
                </div>
                <div className="text-xs font-medium text-gray-500">Avg response time</div>
              </div>
            </div>

            {/* Category rollup chips */}
            {(() => {
              const byCat = suite.results.reduce((acc, r) => {
                const key = CATEGORY_LABELS[r.category] || r.category;
                acc[key] ??= { total: 0, passed: 0, cat: r.category };
                acc[key].total += 1;
                if (r.status === 'success') acc[key].passed += 1;
                return acc;
              }, {});
              const entries = Object.entries(byCat);
              if (entries.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-2 mb-4">
                  {entries.map(([label, v]) => {
                    const style = CATEGORY_STYLES[v.cat] || CATEGORY_STYLES.other;
                    return (
                      <span
                        key={label}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.badge}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                        {label}: {v.passed}/{v.total} passed
                      </span>
                    );
                  })}
                </div>
              );
            })()}

            {/* Search + filter toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="relative w-full sm:w-auto sm:min-w-[260px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search model, response, or error…"
                  className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-sce-green focus:ring-2 focus:ring-sce-green/20 outline-none transition"
                />
              </div>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium self-start">
                {[
                  { key: 'all', label: `All (${suite.results.length})` },
                  { key: 'passed', label: `Passed (${suite.summary.passed})` },
                  { key: 'failed', label: `Failed (${suite.summary.failed})` },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setResultFilter(f.key)}
                    className={`px-3 py-2 transition ${
                      resultFilter === f.key
                        ? 'bg-sce-green text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-400 sm:ml-auto">
                Showing {filteredResults.length} of {suite.results.length}
              </span>
            </div>

            {filteredResults.length === 0 && (
              <div className="text-sm text-gray-500 rounded-2xl border border-gray-200 bg-white px-4 py-6 text-center">
                No results match your filters.
              </div>
            )}

            {/* Desktop table (md+) */}
            {filteredResults.length > 0 && (
              <div className="hidden md:block overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Model</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Result</th>
                      <th className="px-4 py-3 font-semibold" title="How long the model took to respond">Speed</th>
                      <th className="px-4 py-3 font-semibold" title="Total tokens used (input + output)">Tokens</th>
                      <th className="px-4 py-3 font-semibold">Response</th>
                      <th className="px-4 py-3 font-semibold w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredResults.map((r, i) => {
                      const style = CATEGORY_STYLES[r.category] || CATEGORY_STYLES.other;
                      const rowKey = `${r.apiId}-${i}`;
                      const isOpen = !!rowExpanded[rowKey];
                      const preview = r.content || r.responseBody || r.error || '—';
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            className="hover:bg-gray-50/60 transition cursor-pointer"
                            onClick={() =>
                              setRowExpanded((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                            }
                          >
                            <td className="px-4 py-3 font-medium text-gray-800">{r.displayName || r.apiId}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${style.badge}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                                {CATEGORY_LABELS[r.category] || r.category}
                              </span>
                            </td>
                            <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatLatency(r.latencyMs)}</td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs" title={tokenInfo(r.tokenUsage).title}>
                              {tokenInfo(r.tokenUsage).label}
                            </td>
                            <td className={`px-4 py-3 truncate max-w-xs text-xs ${r.status === 'success' ? 'text-gray-600' : 'text-red-600'}`}>
                              {preview}
                            </td>
                            <td className="px-4 py-3 text-gray-400">
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              />
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-gray-50/60">
                              <td colSpan={7} className="px-4 py-4">
                                <div className="grid gap-4 lg:grid-cols-2 text-xs">
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-gray-600">Prompt sent</span>
                                      <CopyButton text={r.promptSent} />
                                    </div>
                                    <pre className="whitespace-pre-wrap break-words text-gray-700 font-mono bg-white border border-gray-100 rounded-lg p-2.5 max-h-48 overflow-auto">
                                      {r.promptSent || '—'}
                                    </pre>
                                  </div>
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-gray-600">Response</span>
                                      <CopyButton text={r.content || r.responseBody} />
                                    </div>
                                    <pre className="whitespace-pre-wrap break-words text-gray-700 font-mono bg-white border border-gray-100 rounded-lg p-2.5 max-h-48 overflow-auto">
                                      {r.content || r.responseBody || '—'}
                                    </pre>
                                  </div>
                                  {r.error && (
                                    <div className="lg:col-span-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-semibold text-red-600">Error</span>
                                        <CopyButton text={r.wwwAuthenticate || r.error} />
                                      </div>
                                      <pre className="whitespace-pre-wrap break-words text-red-600 font-mono bg-red-50 border border-red-100 rounded-lg p-2.5">
                                        {r.error}
                                        {r.wwwAuthenticate ? `\n${r.wwwAuthenticate}` : ''}
                                      </pre>
                                    </div>
                                  )}
                                  <div className="lg:col-span-2 flex flex-wrap gap-x-6 gap-y-1 text-gray-500 pt-1 border-t border-gray-100">
                                    <span>
                                      <span className="font-semibold text-gray-600">HTTP:</span> {r.httpStatus || '—'}
                                    </span>
                                    <span className="flex items-center gap-1.5 min-w-0">
                                      <span className="font-semibold text-gray-600 shrink-0">Endpoint:</span>
                                      <span className="font-mono truncate" title={r.url || ''}>{r.url || '—'}</span>
                                      <CopyButton text={r.url} />
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile cards (<md) */}
            {filteredResults.length > 0 && (
              <div className="md:hidden space-y-3">
                {filteredResults.map((r, i) => {
                  const style = CATEGORY_STYLES[r.category] || CATEGORY_STYLES.other;
                  const rowKey = `m-${r.apiId}-${i}`;
                  const isOpen = !!rowExpanded[rowKey];
                  return (
                    <div key={rowKey} className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <div className="px-4 py-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium text-gray-800 break-words">
                            {r.displayName || r.apiId}
                          </span>
                          <StatusPill status={r.status} />
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium border ${style.badge}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                            {CATEGORY_LABELS[r.category] || r.category}
                          </span>
                          <span>{formatLatency(r.latencyMs)}</span>
                          <span title={tokenInfo(r.tokenUsage).title}>
                            {tokenInfo(r.tokenUsage).label} tokens
                          </span>
                        </div>
                        {r.error && (
                          <div className="text-xs text-red-600 bg-red-50 rounded-md px-2 py-1 break-words">
                            {r.error}
                          </div>
                        )}
                        <button
                          onClick={() =>
                            setRowExpanded((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                          }
                          className="inline-flex items-center gap-0.5 text-xs text-sce-green font-medium hover:underline"
                        >
                          {isOpen ? 'Hide details' : 'View details'}
                          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {isOpen && (
                        <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 space-y-3 text-xs">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-gray-600">Prompt sent</span>
                              <CopyButton text={r.promptSent} />
                            </div>
                            <pre className="whitespace-pre-wrap break-words text-gray-700 font-mono bg-white border border-gray-100 rounded-lg p-2.5 max-h-40 overflow-auto">
                              {r.promptSent || '—'}
                            </pre>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-gray-600">Response</span>
                              <CopyButton text={r.content || r.responseBody} />
                            </div>
                            <pre className="whitespace-pre-wrap break-words text-gray-700 font-mono bg-white border border-gray-100 rounded-lg p-2.5 max-h-40 overflow-auto">
                              {r.content || r.responseBody || '—'}
                            </pre>
                          </div>
                          <div className="flex flex-col gap-1 text-gray-500 pt-1 border-t border-gray-100">
                            <span>
                              <span className="font-semibold text-gray-600">HTTP:</span> {r.httpStatus || '—'}
                            </span>
                            <span className="flex items-center gap-1.5 min-w-0">
                              <span className="font-semibold text-gray-600 shrink-0">Endpoint:</span>
                              <span className="font-mono truncate" title={r.url || ''}>{r.url || '—'}</span>
                              <CopyButton text={r.url} />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </ErrorBoundary>
  );
}
